import { databaseColumns } from '@/BlockEngineAnalytics/columns';
import DataGridFooter from '@/components/common/DataGridFooter';
import Export from '@/components/common/Export';
import SimpleSearchField from '@/components/common/SimpleSearchField';
import { DatabaseLog, DatabaseLogTableInfo } from '@/types/analytics';
import { StripedDataGrid } from '@/util/datagrid';
import { Button, CircularProgress, Stack } from '@mui/material';
import { GridRowParams, GridSortModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import DatabaseLogInfo from './DatabaseLogInfo';

type DatabaseLogsProps = {
  name: string;
  dbLogs: DatabaseLog[];
  selected: boolean;
  isLoading: boolean;
};

const DatabaseLogs: FC<DatabaseLogsProps> = ({ name, dbLogs, selected, isLoading }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string>('');
  const [logs, setLogs] = useState<DatabaseLogTableInfo[]>([]);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [selectedLog, setSelectedLog] = useState<DatabaseLogTableInfo>();
  const [sortModal, setSortModal] = useSessionStorage<GridSortModel>('DatabaseSort', []);
  const [openExport, setOpenExport] = useState<boolean>(false);

  const handleDBLogs = (logs: DatabaseLog[]) => {
    const map = new Map<string, DatabaseLogTableInfo>();

    for (const log of logs) {
      const key = log.step;

      let entry = map.get(key);

      if (!entry) {
        entry = {
          timestamp: log.timestamp,
          step: log.step,
          queryid: log.queryid,
          blockid: log.blockid,
          blockname: log.blockname,
          performance: '',
          query: '',
          response: '',
          returnValue: ''
        };
        map.set(key, entry);
      }

      switch (log.action.toUpperCase()) {
        case 'PERF':
          entry.performance = log.message;
          break;
        case 'QUERY':
          entry.query = log.message;
          break;
        case 'RESP':
          entry.response = log.message;
          break;
        case 'RET':
        case 'ERROR':
          entry.returnValue = log.message;
          break;
      }
    }

    const grouped = Array.from(map.values());

    setLogs(grouped);

    const id = searchParams.get('rowid');
    if (!id) return;

    const foundId = grouped.find((item) => item.step === id);
    if (!foundId) return;

    setSelectedLog(foundId);
  };

  const handleRowClick = (row: DatabaseLogTableInfo) => {
    setSelectedLog(row);

    searchParams.set('rowid', row.step);
    setSearchParams(searchParams);
  };

  const handleCloseDialog = () => {
    setSelectedLog(undefined);

    searchParams.delete('rowid');
    setSearchParams(searchParams);
  };

  const filteredLogs = logs.filter((log) =>
    Object.values(log).some((value) => value.toString().toLowerCase().includes(search.trim().toLowerCase()))
  );

  useEffect(() => handleDBLogs(dbLogs), [dbLogs]);

  return (
    <Stack height={'90%'}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <SimpleSearchField search={search} setSearch={(newSearch) => setSearch(newSearch)} sx={{ width: '50%' }} />
        {filteredLogs.length > 0 && (
          <Button variant='contained' onClick={() => setOpenExport(true)} sx={{ mx: 2 }}>
            Export
          </Button>
        )}
      </Stack>
      {isLoading ? (
        <Stack alignItems='center' mt={10}>
          <CircularProgress color='inherit' />
        </Stack>
      ) : (
        selected && (
          <StripedDataGrid
            columns={databaseColumns()}
            rows={filteredLogs}
            getRowId={(row: GridValidRowModel) => row.step}
            localeText={{ noRowsLabel: 'No reports available' }}
            getRowHeight={() => (showAll ? 'auto' : undefined)}
            sortModel={sortModal}
            onSortModelChange={(newSort: GridSortModel) => setSortModal(newSort)}
            isRowSelectable={() => false}
            onRowDoubleClick={(value: GridRowParams<DatabaseLogTableInfo>) => handleRowClick(value.row)}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            getRowClassName={(params: any) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
            slots={{
              footer: () => (
                <DataGridFooter
                  length={filteredLogs.length}
                  checked={showAll}
                  onChange={(checked) => setShowAll(checked)}
                />
              )
            }}
            sx={{ '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
          />
        )
      )}
      {selectedLog && <DatabaseLogInfo log={selectedLog} onClose={handleCloseDialog} />}
      <Export open={openExport} onClose={() => setOpenExport(false)} name={name} data={filteredLogs} />
    </Stack>
  );
};

export default DatabaseLogs;
